import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { CHAT } from '@/constants/testIds';
import {
  PaperclipIcon,
  MicIcon,
  AudioWaveIcon,
  PlayIcon,
  PauseIcon,
  CloseIcon,
} from '@/lib/icons';
import { fileToDataUrl, blobToDataUrl } from '@/lib/fileToDataUrl';
import { Waveform } from './Waveform';
import { BilingualTooltip } from './BilingualTooltip';
import { http } from '@/lib/api';
import { playSent } from '@/lib/sounds';

const MAX_FILE_MB = 12;

// 75 % of the time the placeholder is "loving you…", the other 25 % is
// randomised between these four phrases.
const ALT_PLACEHOLDERS = [
  'beautiful...',
  "how're you...",
  'talk to me...',
  'kisses...',
];

function pickPlaceholder() {
  if (Math.random() < 0.75) return 'loving you...';
  return ALT_PLACEHOLDERS[Math.floor(Math.random() * ALT_PLACEHOLDERS.length)];
}

function pickMime(preferred) {
  if (typeof MediaRecorder === 'undefined') return '';
  for (const m of preferred) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return '';
}

const AUDIO_MIMES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/ogg',
  'audio/mp4',
];

export const Composer = ({ onSent, deviceId }) => {
  const [text, setText] = useState('');
  const [media, setMedia] = useState([]); // [{kind, mime, data_url, name}]
  const [recordedAudio, setRecordedAudio] = useState(null); // {data_url, mime, duration}
  const [recording, setRecording] = useState(false);
  const [dictating, setDictating] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const [placeholder, setPlaceholder] = useState(() => pickPlaceholder());

  const textareaRef = useRef(null);
  const recorderRef = useRef(null);
  const recordStartRef = useRef(0);
  const recordIntervalRef = useRef(null);
  const recordModeRef = useRef('audio'); // 'audio' for audio post, 'dictate' for dictation
  const previewAudioRef = useRef(null);

  // Auto-grow textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(140, el.scrollHeight)}px`;
  }, [text]);

  // Auto-shrink the textarea font ONLY while the placeholder is visible
  // (i.e. while the user hasn't typed anything yet) so that rotating
  // phrases like "loving you...", "how're you...", "beautiful..." always
  // render on a single line — even on narrow iPhones where the default
  // size would otherwise wrap to two lines.
  //
  // We use a hidden canvas to measure the placeholder's exact rendered
  // width in the textarea's exact computed font, then clamp the font
  // size down by the ratio needed to fit within the available content
  // width. As soon as the user starts typing, we drop the override so
  // their actual content renders at the original (larger) size.
  const renderedPlaceholder = recording
    ? `recording... ${recordSeconds}s`
    : dictating
      ? 'transcribing...'
      : placeholder;
  const [placeholderFontPx, setPlaceholderFontPx] = useState(null);

  useLayoutEffect(() => {
    // If user is typing, restore default sizing immediately.
    if (text.length > 0) {
      setPlaceholderFontPx(null);
      return undefined;
    }
    const el = textareaRef.current;
    if (!el || typeof window === 'undefined') return undefined;

    let canvas = null;

    const measure = () => {
      const styles = window.getComputedStyle(el);
      const padL = parseFloat(styles.paddingLeft) || 0;
      const padR = parseFloat(styles.paddingRight) || 0;
      const available = el.clientWidth - padL - padR;
      if (available <= 0) return;

      const defaultFontPx = parseFloat(styles.fontSize) || 18;
      const weight = styles.fontWeight || 'normal';
      const family = styles.fontFamily || 'sans-serif';
      const style = styles.fontStyle || 'normal';

      if (!canvas) canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.font = `${style} ${weight} ${defaultFontPx}px ${family}`;
      // Add a 4px breathing buffer so we don't sit razor-close to the edge.
      const measured = ctx.measureText(renderedPlaceholder).width + 4;

      if (measured <= available) {
        // Fits comfortably — let CSS govern.
        setPlaceholderFontPx((prev) => (prev == null ? prev : null));
        return;
      }
      // Shrink proportionally; never go below 10px (legibility floor).
      const shrunk = Math.max(10, Math.floor((available / measured) * defaultFontPx));
      setPlaceholderFontPx((prev) => (prev === shrunk ? prev : shrunk));
    };

    measure();

    // Re-measure on viewport changes (rotation, keyboard, narrow → wide).
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => measure());
      ro.observe(el);
    }
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);

    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      if (ro) ro.disconnect();
    };
  }, [text, renderedPlaceholder]);

  // Programmatic file picker — DELIBERATELY does not keep a persistent
  // <input type="file"> in the JSX render tree. Even when hidden +
  // tabIndex=-1 + aria-hidden, iOS Safari still counts a static file
  // input as a focusable form field and shows the iOS form-assistant
  // bar above the keyboard (the bar with up/down field-navigator
  // arrows + a Done/checkmark icon). By creating the input on-demand
  // inside the user's click handler and removing it again the moment
  // the file dialog closes, iOS sees the textarea as the ONLY form
  // field on the page during normal use → no assistant bar.
  const onPickFiles = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,video/*';
    input.multiple = true;
    // Hide it visually + keep it out of any focus / a11y order.
    input.style.position = 'fixed';
    input.style.left = '-9999px';
    input.style.top = '-9999px';
    input.style.opacity = '0';
    input.style.pointerEvents = 'none';
    input.setAttribute('tabindex', '-1');
    input.setAttribute('aria-hidden', 'true');

    // Clean up the element after EITHER a file is selected OR the
    // dialog is cancelled. We can't always detect cancel reliably on
    // iOS, so we also remove on a 'focus' return to the page as a
    // belt-and-braces safety net.
    const cleanup = () => {
      try {
        if (input.parentNode) input.parentNode.removeChild(input);
      } catch (_e) {
        /* no-op */
      }
      window.removeEventListener('focus', onFocusReturn);
    };
    const onFocusReturn = () => {
      // Defer slightly so the change event has a chance to fire first.
      setTimeout(cleanup, 200);
    };

    input.addEventListener('change', async (e) => {
      try {
        await onFilesSelected(e);
      } finally {
        cleanup();
      }
    });
    window.addEventListener('focus', onFocusReturn);

    document.body.appendChild(input);
    // Must be called synchronously within the user-gesture handler so
    // iOS allows the picker to open.
    input.click();
  };

  const onFilesSelected = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = '';
    if (list.length === 0) return;
    const accepted = [];
    for (const f of list) {
      if (f.size > MAX_FILE_MB * 1024 * 1024) {
        toast.error(`${f.name} is over ${MAX_FILE_MB}MB`);
        continue;
      }
      const isImage = f.type.startsWith('image/');
      const isVideo = f.type.startsWith('video/');
      if (!isImage && !isVideo) {
        toast.error(`${f.name}: unsupported file type`);
        continue;
      }
      try {
        const data_url = await fileToDataUrl(f);
        accepted.push({
          kind: isImage ? 'image' : 'video',
          mime: f.type,
          data_url,
          name: f.name,
        });
      } catch (err) {
        toast.error(`Failed to read ${f.name}`);
      }
    }
    if (accepted.length) setMedia((prev) => [...prev, ...accepted]);
  };

  const removeMedia = (idx) => setMedia((prev) => prev.filter((_, i) => i !== idx));

  async function startRecording(mode) {
    if (recording || dictating) return;
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error('Microphone not available.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickMime(AUDIO_MIMES) || '';
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunks.push(e.data);
      };
      rec.onstop = async () => {
        const totalMime = rec.mimeType || mime || 'audio/webm';
        const blob = new Blob(chunks, { type: totalMime });
        const data_url = await blobToDataUrl(blob);
        const duration = (Date.now() - recordStartRef.current) / 1000;
        // Stop the stream
        stream.getTracks().forEach((t) => t.stop());
        if (recordModeRef.current === 'audio') {
          setRecordedAudio({ data_url, mime: totalMime, duration });
          toast.success('Audio recorded. Press send to publish.');
        } else {
          // dictation mode
          setDictating(true);
          try {
            const resp = await http.post('/dictate', { data_url, mime: totalMime });
            const t = (resp.data?.text || '').trim();
            if (t) {
              setText((prev) => (prev ? `${prev} ${t}` : t));
              toast.success('Dictation transcribed.');
            } else {
              toast('No speech detected.');
            }
          } catch (err) {
            toast.error('Dictation failed.');
          } finally {
            setDictating(false);
          }
        }
      };
      recordModeRef.current = mode;
      recordStartRef.current = Date.now();
      setRecordSeconds(0);
      recordIntervalRef.current = window.setInterval(() => {
        setRecordSeconds(Math.floor((Date.now() - recordStartRef.current) / 1000));
      }, 250);
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch (err) {
      toast.error('Microphone permission denied.');
    }
  }

  function stopRecording() {
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') rec.stop();
    if (recordIntervalRef.current) {
      clearInterval(recordIntervalRef.current);
      recordIntervalRef.current = null;
    }
    setRecording(false);
  }

  const onAudioBtnClick = () => {
    if (recording && recordModeRef.current === 'audio') {
      stopRecording();
    } else if (!recording && !dictating) {
      startRecording('audio');
    }
  };

  const onMicBtnClick = () => {
    if (recording && recordModeRef.current === 'dictate') {
      stopRecording();
    } else if (!recording && !dictating) {
      startRecording('dictate');
    }
  };

  const togglePreview = () => {
    const el = previewAudioRef.current;
    if (!el) return;
    if (el.paused) {
      el.play().catch(() => {});
      setPreviewPlaying(true);
    } else {
      el.pause();
      setPreviewPlaying(false);
    }
  };

  useEffect(() => {
    const el = previewAudioRef.current;
    if (!el) return undefined;
    const onEnd = () => setPreviewPlaying(false);
    el.addEventListener('ended', onEnd);
    return () => el.removeEventListener('ended', onEnd);
  }, [recordedAudio?.data_url]);

  const canSend = !sending && (text.trim() || media.length > 0 || recordedAudio);

  const onSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      let audioPayload = null;
      if (recordedAudio) {
        // Transcribe audio + translate
        try {
          const tr = await http.post('/transcribe', {
            data_url: recordedAudio.data_url,
            mime: recordedAudio.mime,
            translate: true,
          });
          audioPayload = {
            data_url: recordedAudio.data_url,
            mime: recordedAudio.mime,
            duration: recordedAudio.duration,
            language: tr.data?.language || null,
            text: tr.data?.text || '',
            translation: tr.data?.translation || '',
            translated_lang: tr.data?.translated_lang || null,
            words: tr.data?.words || [],
            translation_words: tr.data?.translation_words || [],
          };
        } catch (err) {
          // Fall back to audio without transcript
          audioPayload = {
            data_url: recordedAudio.data_url,
            mime: recordedAudio.mime,
            duration: recordedAudio.duration,
            text: '',
            translation: '',
            words: [],
            translation_words: [],
          };
          toast.error('Could not transcribe audio (saved without transcript).');
        }
      }

      const body = {
        device_id: deviceId,
        text: text.trim(),
        media,
        audio: audioPayload,
        author_name: '',
      };

      const resp = await http.post('/posts', body);
      // Fire the send sound (piano + sparkly swoosh, overlaid in the
      // same key). The Composer's onClick is a user gesture so iOS will
      // allow the AudioContext to play. No-op if sound is toggled off.
      playSent();
      onSent?.(resp.data);
      setText('');
      setMedia([]);
      setRecordedAudio(null);
      setPreviewPlaying(false);
      setPlaceholder(pickPlaceholder()); // fresh phrase after every send
    } catch (err) {
      toast.error('Could not send post.');
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  return (
    <div className="composer-wrap">
      <form
        className="composer"
        data-testid={CHAT.composer}
        onSubmit={(e) => {
          e.preventDefault();
          onSend();
        }}
      >
        {media.length > 0 && (
          <div className="composer__attachments" data-testid={CHAT.attachmentStrip}>
            {media.map((m, i) => (
              <div
                key={i}
                className="composer__attachment"
                data-testid={CHAT.attachmentItem}
              >
                {m.kind === 'image' ? (
                  <img src={m.data_url} alt={m.name || 'preview'} />
                ) : (
                  <video src={m.data_url} muted playsInline preload="metadata" />
                )}
                <button
                  type="button"
                  className="composer__attachment-remove"
                  onClick={() => removeMedia(i)}
                  aria-label="Remove attachment"
                  data-testid={CHAT.attachmentRemove}
                >
                  <CloseIcon size={10} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="composer__row">
          <div className="composer__buttons">
            <BilingualTooltip en="Image & video" ru="Фото и видео">
              <button
                type="button"
                className="icon-btn"
                onClick={onPickFiles}
                aria-label="Attach image or video"
                data-testid={CHAT.composerAttach}
                disabled={sending}
              >
                <PaperclipIcon size={20} />
              </button>
            </BilingualTooltip>
            <BilingualTooltip en="Voice dictation" ru="Голосовой ввод">
              <button
                type="button"
                className={`icon-btn ${dictating || (recording && recordModeRef.current === 'dictate') ? 'icon-btn--dictating' : ''}`}
                onClick={onMicBtnClick}
                aria-label={recording && recordModeRef.current === 'dictate' ? 'Stop dictation' : 'Voice dictation'}
                data-testid={CHAT.composerDictate}
                disabled={sending || dictating || (recording && recordModeRef.current === 'audio')}
              >
                <MicIcon size={18} />
              </button>
            </BilingualTooltip>
            <BilingualTooltip en="Audio post" ru="Аудио сообщение">
              <button
                type="button"
                className={`icon-btn ${recording && recordModeRef.current === 'audio' ? 'icon-btn--recording' : ''}`}
                onClick={onAudioBtnClick}
                aria-label={recording && recordModeRef.current === 'audio' ? 'Stop recording' : 'Record audio post'}
                data-testid={CHAT.composerRecord}
                disabled={sending || dictating || (recording && recordModeRef.current === 'dictate') || !!recordedAudio}
              >
                <AudioWaveIcon size={20} />
              </button>
            </BilingualTooltip>
          </div>

          {recordedAudio ? (
            <div
              className="composer__audio-inline"
              data-testid={CHAT.audioPreview}
            >
              <button
                type="button"
                className="composer__audio-inline__play"
                onClick={togglePreview}
                aria-label={previewPlaying ? 'Pause preview' : 'Play preview'}
                data-testid={CHAT.audioPreviewPlay}
              >
                {previewPlaying ? <PauseIcon size={16} /> : <PlayIcon size={16} />}
              </button>
              <Waveform
                duration={recordedAudio.duration}
                maxWidth={420}
                height={22}
                className="composer__audio-inline__wave"
              />
              <span className="composer__audio-inline__time">
                {formatDur(recordedAudio.duration)}
              </span>
              <button
                type="button"
                className="composer__audio-inline__discard"
                onClick={() => {
                  setRecordedAudio(null);
                  setPreviewPlaying(false);
                }}
                aria-label="Discard recording"
                data-testid={CHAT.audioPreviewDiscard}
              >
                <span aria-hidden="true">×</span>
              </button>
              <audio
                ref={previewAudioRef}
                src={recordedAudio.data_url}
                preload="metadata"
              />
            </div>
          ) : (
            <textarea
              ref={textareaRef}
              className="composer__input"
              placeholder={renderedPlaceholder}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={onKeyDown}
              // Defensive attributes to discourage iOS from showing the
              // form-assistant bar above the keyboard. Together with the
              // hidden file input's tabIndex=-1, these tell iOS this is
              // a single, standalone text field with no autofill / nav
              // affordances needed.
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="sentences"
              spellCheck={false}
              enterKeyHint="send"
              inputMode="text"
              style={
                placeholderFontPx != null
                  ? { fontSize: `${placeholderFontPx}px` }
                  : undefined
              }
              onFocus={() => {
                /* Intentionally no-op.
                   Previously this handler called `scrollIntoView({block:
                   'center'})` 320 ms after focus as a safety net to keep
                   the textarea visible above the keyboard. With the
                   composer now `position: absolute` inside a viewport-
                   locked `.app-frame` (see App.css) AND the visualViewport
                   listener in App.js committing `--kb-offset` once the
                   keyboard settles, the composer is ALREADY guaranteed
                   to sit directly above the keyboard. Calling
                   scrollIntoView on top of that introduces a competing
                   programmatic scroll that iOS Safari (PWA standalone)
                   was using to drag the fixed-positioned ancestor — which
                   the user perceived as the composer "floating around"
                   during scroll. Removing this handler eliminates that
                   source of motion entirely. */
              }}
              rows={1}
              data-testid={CHAT.composerInput}
              disabled={sending}
            />
          )}

          <BilingualTooltip en="Send" ru="Отправить">
            <button
              type="submit"
              className="send-btn"
              aria-label="Send"
              data-testid={CHAT.composerSend}
              disabled={!canSend}
              title="Send (Enter)"
            >
              🖤
            </button>
          </BilingualTooltip>
        </div>
        {recording && (
          <div
            data-testid={CHAT.recordingIndicator}
            style={{ display: 'none' }}
            aria-hidden="true"
          >
            recording {recordSeconds}s
          </div>
        )}
      </form>
    </div>
  );
};

function formatDur(seconds) {
  if (!seconds || !Number.isFinite(seconds)) return '0:00';
  const total = Math.max(0, Math.round(seconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}
